import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Platform } from 'react-native';
import { usePathname, useRootNavigationState, useRouter } from 'expo-router';
import { getCurrentRedirectTo, type PartialFunnelContext } from './funnelContext';
import { isValidEmailAddress, normalizeEmailAddress } from '../utils/email';
import type { LocalizedMessage } from './i18n/messages';

interface AuthConfig {
    mode?: 'signin' | 'signup';
    redirectTo?: string;
    forSwitchAccount?: boolean;
    funnelContext?: PartialFunnelContext;
    loginHint?: string;
    copyVariant?: 'default' | 'forwarded';
}

export interface ConfirmationMessage {
    type: 'email_confirmed' | 'account_created';
    title: LocalizedMessage;
    message: LocalizedMessage;
}

interface AuthContextType {
    showAuthModal: boolean;
    authModalConfig: AuthConfig;
    openAuth: (config?: AuthConfig) => void;
    closeAuth: () => void;
    confirmationMessage: ConfirmationMessage | null;
    setConfirmation: (message: ConfirmationMessage | null) => void;
    clearConfirmation: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface ForwardedAuthBootstrap {
    config: AuthConfig;
    canonicalRoute: string;
}

function getForwardedAuthBootstrap(): ForwardedAuthBootstrap | null {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return null;
    }

    const url = new URL(window.location.href);
    if (url.searchParams.get('auth') !== 'forwarded') {
        return null;
    }

    const rawEmail = url.searchParams.get('login_hint');
    const normalizedEmail = rawEmail ? normalizeEmailAddress(rawEmail) : '';
    const config: AuthConfig = {
        mode: url.searchParams.get('auth_mode') === 'signin' ? 'signin' : 'signup',
        redirectTo: url.searchParams.get('redirect_to') || '/dashboard/onboarding/source-connected',
        loginHint: normalizedEmail && isValidEmailAddress(normalizedEmail)
            ? normalizedEmail
            : undefined,
        copyVariant: 'forwarded',
    };

    url.searchParams.delete('auth');
    url.searchParams.delete('auth_mode');
    url.searchParams.delete('login_hint');
    url.searchParams.delete('redirect_to');
    const nextRoute = `${url.pathname}${url.search}${url.hash}` || '/';

    return { config, canonicalRoute: nextRoute };
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const router = useRouter();
    const rootNavigationState = useRootNavigationState();
    const forwardedAuthParsedRef = useRef(false);
    const [forwardedAuthBootstrap, setForwardedAuthBootstrap] = useState<ForwardedAuthBootstrap | null>(null);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authModalConfig, setAuthModalConfig] = useState<AuthConfig>({});
    const [confirmationMessage, setConfirmationMessage] = useState<ConfirmationMessage | null>(null);
    const pathname = usePathname();

    useEffect(() => {
        if (forwardedAuthParsedRef.current) {
            return;
        }
        forwardedAuthParsedRef.current = true;

        const bootstrap = getForwardedAuthBootstrap();
        if (!bootstrap) {
            return;
        }

        setForwardedAuthBootstrap(bootstrap);
        setAuthModalConfig(bootstrap.config);
        setShowAuthModal(true);

        // Strip the email-bearing entry parameters as soon as the client has
        // captured them. The provider keeps the modal state across the
        // subsequent Expo Router synchronization.
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.history.replaceState(window.history.state, '', bootstrap.canonicalRoute);
        }
    }, []);

    useEffect(() => {
        if (!forwardedAuthBootstrap) {
            return;
        }

        if (!rootNavigationState?.key) {
            return;
        }

        // Keep Expo Router's navigation state in sync with the canonical URL.
        // A history-only replacement is overwritten when the router hydrates.
        // This provider owns the modal state and persists across the route update.
        router.replace(forwardedAuthBootstrap.canonicalRoute);
    }, [forwardedAuthBootstrap, rootNavigationState?.key, router]);

    const openAuth = (config: AuthConfig = {}) => {
        const redirectTo = config.redirectTo || getCurrentRedirectTo(pathname || '/');
        setAuthModalConfig({
            mode: config.mode || 'signin',
            redirectTo,
            forSwitchAccount: config.forSwitchAccount,
            funnelContext: config.funnelContext,
            loginHint: config.loginHint,
            copyVariant: config.copyVariant,
        });
        setShowAuthModal(true);
    };

    const closeAuth = () => {
        setShowAuthModal(false);
        setAuthModalConfig({});
    };

    const clearConfirmation = () => {
        setConfirmationMessage(null);
    };

    const value = {
        showAuthModal,
        authModalConfig,
        openAuth,
        closeAuth,
        confirmationMessage,
        setConfirmation: setConfirmationMessage,
        clearConfirmation,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
